import { combineReducers } from 'redux';
import { connectRouter } from 'connected-react-router';
import { IPtw } from '@relab/alstom-ptw-shared';

import { ActionType, AppActionTypes, AppState, RequestAction, ServerRequestStatus } from './types';
import { client } from './store';

const initialAppState: AppState = {
  server: {
    status: 'unknown',
    requests: {},
  },
  user: null,
  projects: [],
  ehss: [],
};

function setStateServerActionStatus(
  state: AppState,
  actionType: ActionType,
  requestStatus: ServerRequestStatus
): AppState {
  return {
    ...state,
    server: {
      ...state.server,
      requests: {
        ...state.server.requests,
        [actionType]: requestStatus,
      },
    },
  };
}

function appReducer(state = initialAppState, action: AppActionTypes): AppState {
  const requestAction: RequestAction = action as RequestAction;

  if (requestAction.payload && requestAction.payload.request && !requestAction.meta) {
    state = setStateServerActionStatus(state, action.type, {
      error: null,
      pending: true,
      success: false,
    });
  } else {
    if (!!requestAction.meta && !!requestAction.error) {
      state = setStateServerActionStatus(state, requestAction.meta.previousAction.type, {
        error: requestAction.error.response?.data || 'Error',
        pending: false,
        success: false,
      });
    } else if (!!requestAction.meta && !!requestAction.payload) {
      state = setStateServerActionStatus(state, requestAction.meta.previousAction.type, {
        error: null,
        pending: false,
        success: true,
        payload: requestAction.payload,
      });
    }
  }

  // Workaround
  // @ts-ignore
  if (action.type === '@@router/LOCATION_CHANGE') {
    return {
      ...state,
      project: undefined,
      ptw: undefined,
      ehs: undefined,
      server: {
        ...state.server,
        requests: {},
      },
    };
  }

  switch (action.type) {
    case ActionType.SERVER_STATE_SUCCESS:
      return {
        ...state,
        server: {
          ...state.server,
          status: action.payload.data.message,
        },
      };

    case ActionType.RESET_SERVER_REQUEST:
      return setStateServerActionStatus(state, action.payload, {
        error: null,
        pending: false,
        success: false,
      });

    case ActionType.SIGN_IN_SUCCESS:
      const { jwtToken, user } = action.payload.data;

      localStorage.setItem('Authorization', `Bearer ${jwtToken}`);
      client.defaults.headers.common['Authorization'] = `Bearer ${jwtToken}`;

      return {
        ...state,
        user,
      };

    case ActionType.SIGN_OUT:
      localStorage.removeItem('Authorization');
      client.defaults.headers.common['Authorization'] = null;

      return {
        ...state,
        user: null,
      };

    case ActionType.SIGN_UP_SUCCESS:
      return {
        ...state,
        users: [...state.users, action.payload.data],
      };

    case ActionType.CHECK_LOGIN_SUCCESS:
      return {
        ...state,
        user: action.payload.data,
      };

    case ActionType.USERS_SUCCESS:
      return {
        ...state,
        users: action.payload.data,
      };

    case ActionType.USER_EDIT_SUCCESS:
      return {
        ...state,
        users: state.users?.map((user) => {
          if (user._id === action.meta.previousAction.payload.user._id) {
            return action.meta.previousAction.payload.user;
          } else {
            return user;
          }
        }),
        user:
          state.user._id === action.meta.previousAction.payload.user._id
            ? action.meta.previousAction.payload.user
            : state.user,
      };

    case ActionType.USER_DELETE_SUCCESS:
      return {
        ...state,
        users: state.users.filter((user) => user._id !== action.meta.previousAction.payload.user._id),
      };

    case ActionType.PROJECT_CREATE_SUCCESS:
      return {
        ...state,
        projects: [...state.projects, action.payload.data],
      };

    case ActionType.PROJECT_SUCCESS:
      return {
        ...state,
        project: action.payload.data,
      };

    case ActionType.PROJECTS_SUCCESS:
      return {
        ...state,
        projects: action.payload.data,
      };

    case ActionType.PROJECT_UPSERT:
      return {
        ...state,
        projects: state.projects.map((project) => {
          if (project._id === action.payload.project._id) {
            return action.payload.project;
          } else {
            return project;
          }
        }),
      };

    case ActionType.PROJECT_DELETE_SUCCESS:
      return {
        ...state,
        projects: state.projects.filter((project) => project._id !== action.meta.previousAction.payload.project._id),
      };

    case ActionType.WORKSTATION_CREATE_SUCCESS:
      return {
        ...state,
        workstations: [...state.workstations, action.payload.data],
      };

    case ActionType.WORKSTATIONS_SUCCESS:
      return {
        ...state,
        workstations: action.payload.data,
      };

    case ActionType.WORKSTATION_UPSERT:
      return {
        ...state,
        workstations: state.workstations.map((workstation) => {
          if (workstation._id === action.payload.workstation._id) {
            return action.payload.workstation;
          } else {
            return workstation;
          }
        }),
      };

    case ActionType.WORKSTATION_DELETE_SUCCESS:
      return {
        ...state,
        workstations: state.workstations.filter(
          (workstation) => workstation._id !== action.meta.previousAction.payload.workstation._id
        ),
      };

    case ActionType.BUILDINGS_SUCCESS:
      return {
        ...state,
        buildings: action.payload.data,
      };

    case ActionType.BUILDING_UPSERT:
      return {
        ...state,
        buildings:
          typeof state.buildings.find((building) => building._id === action.payload.building._id) === 'undefined'
            ? [...state.buildings, action.payload.building]
            : state.buildings.map((building) => {
                if (building._id === action.payload.building._id) {
                  return action.payload.building;
                } else {
                  return building;
                }
              }),
      };

    case ActionType.BUILDING_DELETE_SUCCESS:
      return {
        ...state,
        buildings: state.buildings.filter((building) => building._id !== action.meta.previousAction.payload.buildingId),
      };

    case ActionType.ASSIGNEE_ADD_SUCCESS:
      return {
        ...state,
        project: {
          ...state.project,
          assignees: action.payload.data,
        },
      };

    case ActionType.ASSIGNEE_REMOVE_SUCCESS:
      return {
        ...state,
        project: {
          ...state.project,
          assignees: state.project.assignees.filter(
            (assignee) => assignee._id !== action.meta.previousAction.payload.assigneeId
          ),
        },
      };

    case ActionType.TRAIN_CREATE_SUCCESS:
      return {
        ...state,
        project: {
          ...state.project,
          trains: action.payload.data,
        },
      };

    case ActionType.TRAIN_DELETE_SUCCESS:
      return {
        ...state,
        project: {
          ...state.project,
          trains: state.project.trains.filter((train) => train._id !== action.meta.previousAction.payload.trainId),
        },
      };

    case ActionType.PTW_SUCCESS:
      return {
        ...state,
        ptw: action.payload.data,
      };

    case ActionType.PTW_EDIT_SUCCESS:
      return {
        ...state,
        ptw: action.payload.data,
        ptws: !!state.ptws
          ? state.ptws.map((ptw) => (ptw._id === action.payload.data._id ? action.payload.data : ptw))
          : state.ptws,
      };

    case ActionType.PTW_UPDATE:
      return {
        ...state,
        ptw: action.payload.ptw as IPtw,
        ptws: !!state.ptws
          ? state.ptws.map((ptw) => (ptw._id === action.payload.ptw._id ? (action.payload.ptw as IPtw) : ptw))
          : state.ptws,
      };

    case ActionType.EHS_SUCCESS:
      return {
        ...state,
        ehs: action.payload.data,
      };

    case ActionType.EHSS_SUCCESS:
      return {
        ...state,
        ehss: action.payload.data,
      };

    case ActionType.EHS_UPDATE_SUCCESS:
      return {
        ...state,
        ehs: action.payload.data,
        ehss: !!state.ehss
          ? state.ehss.map((ehs) => (ehs._id === action.payload.data._id ? action.payload.data : ehs))
          : state.ehss,
      };

    default:
      return state;
  }
}

export default (history: any) =>
  combineReducers({
    router: connectRouter(history),
    app: appReducer,
  });
